import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { parse } from 'query-string';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import MerchantLayout from '../../../layout/MerchantLayout';
import { ROUTES } from '../../../constants/route';
import { loadDynamicZxcvbn } from '../../../utils/load-zxcvbn';
import { merchantResetPassword } from '../../../services/c2dcrm';
import MerchantSetPasswordView from '../MerchantSetPassword/MerchantSetPasswordView';
import { useSnackbar } from '../../Context/SnackbarContext';

declare const zxcvbn: (pass: string) => { score: number };

const useStyles = makeStyles({
  root: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emailWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '40px',
  },
});

type Query = {
  email?: string | null;
};

type MerchantLoginProps = {
  passwordStrength: (password: string) => { score: number };
};

export const MerchantResetPassword: React.FC<MerchantLoginProps> = ({ passwordStrength }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch<{ resetToken: string }>(ROUTES.MERCHANT_RESET_PASSWORD);
  const { openSnackbar } = useSnackbar();

  const { email } = parse(location.search) as Query;

  const onSetPassword = async (password: string) => {
    try {
      if (!match?.params?.resetToken) {
        openSnackbar('No reset token provided.');
        return;
      }

      const res = await merchantResetPassword(password, match.params.resetToken);

      if (res) {
        history.push(ROUTES.MERCHANT_LOGIN);
      }
    } catch (e) {
      openSnackbar('Error: Something went wrong while resetting the password.');
    }
  };

  return (
    <MerchantLayout>
      <div className={classes.emailWrapper}>
        <Typography variant="h3" color="textPrimary">
          {email}
        </Typography>
      </div>

      <div className={classes.root}>
        <MerchantSetPasswordView
          title="Enter your new password for your merchant account"
          onSetPassword={onSetPassword}
          passwordStrength={passwordStrength}
          buttonText="Save Password"
        />
      </div>
    </MerchantLayout>
  );
};

const MerchantResetPasswordContainer: React.FC = () => {
  const [zxcvbnReady, setZxcvbnReady] = useState(false);

  useEffect(() => {
    loadDynamicZxcvbn(() => {
      setZxcvbnReady(true);
    });
  }, []);

  if (!zxcvbnReady) return null;

  return <MerchantResetPassword passwordStrength={zxcvbn} />;
};

export default MerchantResetPasswordContainer;
